import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { User, Save } from 'lucide-react';
import CustomerLayout from '@/components/layouts/CustomerLayout';
import { customerService } from '@/lib/database';
import type { Customer } from '@/lib/supabase';

export default function CustomerProfile() {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Mock customer ID - in real app, this would come from auth
  const customerId = localStorage.getItem('userId') || 'c1000000-0000-0000-0000-000000000001';

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await customerService.getById(customerId);
      setCustomer(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!customer) return;

    try {
      setSaving(true);
      await customerService.update(customer.id, customer);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof Customer, value: string) => {
    if (!customer) return;
    setCustomer(prev => prev ? { ...prev, [field]: value } : null);
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading profile...</div>
        </div>
      </CustomerLayout>
    );
  }

  if (!customer) {
    return (
      <CustomerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Profile not found</div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600">Manage your account information and preferences</p>
        </div>

        {/* Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Personal Information
            </CardTitle>
            <CardDescription>
              Update your personal details and shipping preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={customer.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={customer.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                />
              </div>
              {/* 
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={customer.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={customer.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>*/}
            </div>

            {/* Addresses */}
            {/* <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Addresses</h3>

              <div className="space-y-2">
                <Label htmlFor="us_address">US Address</Label>
                <Textarea
                  id="us_address"
                  value={customer.us_address}
                  onChange={(e) => handleInputChange('us_address', e.target.value)}
                  placeholder="Your US shipping address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="haiti_address">Haiti Address</Label>
                <Textarea
                  id="haiti_address"
                  value={customer.haiti_address}
                  onChange={(e) => handleInputChange('haiti_address', e.target.value)}
                  placeholder="Your delivery address in Haiti"
                />
              </div>
            </div> */}

            {/* Preferences */}
            {/* <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Preferences</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="preferred_language">Preferred Language</Label>
                  <Select
                    value={customer.preferred_language}
                    onValueChange={(value: 'english' | 'french' | 'creole') => handleInputChange('preferred_language', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">English</SelectItem>
                      <SelectItem value="french">French</SelectItem>
                      <SelectItem value="creole">Haitian Creole</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notification_method">Notification Method</Label>
                  <Select
                    value={customer.notification_method}
                    onValueChange={(value: 'email' | 'sms' | 'both') => handleInputChange('notification_method', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferred_shipping_method">Preferred Shipping</Label>
                  <Select
                    value={customer.preferred_shipping_method}
                    onValueChange={(value: 'air' | 'cargo') => handleInputChange('preferred_shipping_method', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="air">Air Shipping</SelectItem>
                      <SelectItem value="cargo">Sea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
          
            </div> */}

            {/* Special Instructions */}
            <div className="space-y-2">
              <Label htmlFor="special_instructions">Special Instructions</Label>
              <Textarea
                id="special_instructions"
                value={customer.special_instructions || ''}
                onChange={(e) => handleInputChange('special_instructions', e.target.value)}
                placeholder="Any special delivery instructions or notes"
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#254164] hover:bg-[#1a2d47]"
              >
                {saving ? (
                  <>
                    <Save className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </CustomerLayout>
  );
}